// /welcome/github → the authed /installations/confirm, bound to the install's signed state.
import { relayHttpBaseFromWsUrl } from "$lib/core/client.js";
import { resolveShareOrigin } from "$lib/page-share-url.js";
import { reportUiFailure } from "$lib/report-failure.js";

export interface PendingGithubInstall {
  workspaceId: string;
  installationId: string;
  state: string;
}

export type GithubConfirmOutcome = "ok" | "conflict" | "forbidden" | "failed";

export const GITHUB_INSTALL_PARAM = "github_installation";
export const GITHUB_STATE_PARAM = "github_state";

export function isInstallationId(v: string | null): v is string {
  return v !== null && /^\d{1,20}$/.test(v);
}

/** The install the return page was handed, or null (not an install, malformed, or unsigned). */
export function readInstallReturn(params: URLSearchParams): PendingGithubInstall | null {
  const installationId = params.get("installation_id");
  const workspaceId = params.get("workspace") ?? "";
  const state = params.get("state") ?? "";
  if (params.get("github") !== "installed" || !isInstallationId(installationId)) return null; // null: not an install
  if (!/^[A-Za-z0-9_-]{1,128}$/.test(workspaceId) || !state) return null; // null: malformed or unsigned
  return { workspaceId, installationId, state };
}

function tasksPath(p: PendingGithubInstall): string {
  const q = new URLSearchParams({
    [GITHUB_INSTALL_PARAM]: p.installationId,
    [GITHUB_STATE_PARAM]: p.state,
  });
  return `/workspace/${encodeURIComponent(p.workspaceId)}/tasks?${q.toString()}`;
}

export function finishInCyborgUrl(p: PendingGithubInstall): string {
  return `${resolveShareOrigin()}${tasksPath(p)}`;
}

/** Tasks in the desktop app, via its registered `cyborg://app` scheme (desktop-cyborg deep-link.ts). */
export function finishInDesktopUrl(p: PendingGithubInstall): string {
  return `cyborg://app${tasksPath(p)}`;
}

/** "ok" only when the relay recorded it; every other outcome is reported. */
export async function confirmGithubInstall(
  httpBase: string,
  token: string,
  p: PendingGithubInstall,
): Promise<GithubConfirmOutcome> {
  const ids = { workspace_id: p.workspaceId, installation_id: p.installationId };
  try {
    const resp = await fetch(`${httpBase}/api/github/installations/confirm`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(p),
    });
    const body = (resp.ok ? await resp.json() : {}) as { ok?: boolean };
    if (resp.ok && body.ok === true) return "ok";
    reportUiFailure(
      "github.install_return.confirm",
      new Error(`confirm refused: ${resp.status}`),
      ids,
    );
    if (resp.status === 409) return "conflict";
    return resp.status === 403 ? "forbidden" : "failed";
  } catch (e) {
    reportUiFailure("github.install_return.confirm", e, ids);
    return "failed";
  }
}

export function confirmWithSession(
  session: { url: string; token: string },
  p: PendingGithubInstall,
): Promise<GithubConfirmOutcome> {
  return confirmGithubInstall(relayHttpBaseFromWsUrl(session.url), session.token, p);
}

export const CONFIRM_FAILURE_COPY: Record<Exclude<GithubConfirmOutcome, "ok">, string> = {
  conflict: "This GitHub account is already connected to another Cyborg workspace.",
  forbidden: "This link isn't for your account. Start connecting GitHub again from Cyborg.",
  failed: "GitHub didn't finish connecting. Try connecting it again.",
};
